import type React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { EditorVersionChip } from '../../operationEditorMeta';

type VersionChipProps = {
    chip: EditorVersionChip;
};

/**
 * Which version the editor is showing — `v4 draft`, `v3 active`, `Blocked`.
 *
 * This rides in the navbar, beside the route's own title, because "Operation
 * template" alone stops being a complete answer the moment a draft exists: the
 * screen then holds an unpublished recipe that the catalog behind it does not
 * show. See `layout/navbarSlot`.
 *
 * A dot before the word rather than a coloured background alone, so the state is
 * still legible to a reader who cannot separate amber from green.
 */
const TONE: Record<EditorVersionChip['tone'], { chip: string; dot: string }> = {
    draft: {
        chip: 'bg-warning-soft text-warning-soft-foreground',
        dot: 'bg-warning',
    },
    active: {
        chip: 'bg-success-soft text-success-soft-foreground',
        dot: 'bg-success',
    },
    blocked: {
        chip: 'bg-surface-secondary text-muted',
        dot: 'bg-muted',
    },
    none: {
        chip: 'bg-surface-secondary text-muted',
        dot: 'bg-muted',
    },
};

export const VersionChip: React.FC<VersionChipProps> = ({ chip }) => {
    const { t } = useTranslation('pages');
    const tone = TONE[chip.tone];

    return (
        <span
            className={cn(
                'inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1',
                'text-[10px] font-bold uppercase tracking-widest',
                tone.chip,
            )}
            {...getNodeTestIdProp('operation-editor-version-chip')}
        >
            <span
                aria-hidden
                className={cn('size-1.5 rounded-full', tone.dot)}
            />
            {t(chip.labelKey, { version: chip.version })}
        </span>
    );
};
